import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EduContent } from '@campus/dal';
import { LearnosityOverlayData, LearnosityOverlayService } from '../../services/learnosity-overlay.service';
import { LearnosityDialogComponent } from '../learnosity-dialog/learnosity-dialog.component';

@Component({
  standalone: false,
  selector: 'campus-learnosity-overlay',
  templateUrl: './learnosity-overlay.component.html',
  styleUrls: ['./learnosity-overlay.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LearnosityOverlayComponent implements OnDestroy {
  readonly overlayService = inject(LearnosityOverlayService);
  readonly isVisible = signal(false);
  readonly isClosing = signal(false);

  @ViewChild('overlayPanel') overlayPanel: ElementRef<HTMLElement>;
  @ViewChild(LearnosityDialogComponent) dialogComponent: LearnosityDialogComponent;

  private destroyRef = inject(DestroyRef);
  private previouslyFocusedElement: HTMLElement | null = null;
  private originalBodyOverflow = '';
  private isScrollLocked = false;

  constructor() {
    effect(() => {
      if (this.overlayService.isOpen()) {
        this.onOverlayOpened();
      }
    });

    this.overlayService.closed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onOverlayClosed());
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
    this.restorePreviousFocus();
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (!this.overlayService.isOpen()) return;
    this.dialogComponent?.onCloseClicked(false);
  }

  onLeaveAnimationEnd(): void {
    this.isVisible.set(false);
    this.isClosing.set(false);
    this.unlockBodyScroll();
    this.restorePreviousFocus();
  }

  get overlayData(): LearnosityOverlayData | null {
    return this.overlayService.data();
  }

  get eduContent(): EduContent | null {
    const raw = this.overlayData?.eduContent;
    return raw ? EduContent.toEduContent(raw) : null;
  }

  private onOverlayOpened(): void {
    this.isClosing.set(false);
    this.savePreviousFocus();
    this.lockBodyScroll();
    this.isVisible.set(true);

    requestAnimationFrame(() => {
      this.overlayPanel?.nativeElement?.focus();
    });
  }

  private onOverlayClosed(): void {
    this.isClosing.set(true);
  }

  private savePreviousFocus(): void {
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
  }

  private restorePreviousFocus(): void {
    this.previouslyFocusedElement?.focus?.();
    this.previouslyFocusedElement = null;
  }

  private lockBodyScroll(): void {
    if (this.isScrollLocked) return;
    this.originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.isScrollLocked = true;
  }

  private unlockBodyScroll(): void {
    if (!this.isScrollLocked) return;
    document.body.style.overflow = this.originalBodyOverflow;
    this.isScrollLocked = false;
  }
}
